export const GLB_MIME = "model/gltf-binary";
export const MAX_GLB_UPLOAD_BYTES = 128 * 1024 * 1024;
export const MAX_GLB_PREVIEW_BYTES = 64 * 1024 * 1024;

/** Only these locally authored messages may be shown in viewer/upload errors. */
export class ThreeDModelError extends Error {}

export interface EmbeddedModelDocument {
  asset: { version: string };
  buffers: { byteLength: number }[];
  bufferViews?: { buffer: number; byteOffset?: number; byteLength: number }[];
  accessors?: { count: number }[];
  images?: { bufferView: number; mimeType: string }[];
  textures?: { source: number; sampler?: number }[];
  samplers?: { magFilter?: number; minFilter?: number; wrapS?: number; wrapT?: number }[];
  materials?: unknown[];
  meshes: { primitives: { attributes: { POSITION: number }; indices?: number; mode?: number }[] }[];
}

/** Defense in depth after exact bytes match server custody; not GLB admission. */
export function inspectViewerGLB(data: ArrayBuffer): { document: EmbeddedModelDocument; binaryOffset: number; triangles: number } {
  const fail = (detail = "This model cannot be safely previewed."): never => { throw new ThreeDModelError(detail); };
  if (data.byteLength < 28 || data.byteLength > MAX_GLB_PREVIEW_BYTES) fail("Browser preview supports models up to 64 MiB.");
  const view = new DataView(data);
  if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(4, true) !== 2 || view.getUint32(8, true) !== data.byteLength) fail();
  const length = view.getUint32(12, true); const binaryHeader = 20 + length;
  if (!length || length > 4 * 1024 * 1024 || length % 4 || binaryHeader + 8 > data.byteLength || view.getUint32(16, true) !== 0x4e4f534a || view.getUint32(binaryHeader + 4, true) !== 0x004e4942) fail();
  const binaryLength = view.getUint32(binaryHeader, true);
  if (!binaryLength || binaryLength % 4 || binaryHeader + 8 + binaryLength !== data.byteLength) fail();
  const raw: unknown = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(new Uint8Array(data, 20, length)));
  let budget = 200_000;
  function walk(value: unknown, depth: number) {
    if (--budget < 0 || depth > 64) fail();
    if (Array.isArray(value)) { if (value.length > 4096) fail(); for (const item of value) walk(item, depth + 1); }
    else if (value && typeof value === "object") {
      const entries = Object.entries(value); if (entries.length > 4096) fail();
      for (const [key, item] of entries) {
        if (key === "uri" || key === "extensions" || (key === "extensionsUsed" || key === "extensionsRequired") && (!Array.isArray(item) || item.length)) fail("This preview supports embedded, uncompressed GLB models only.");
        walk(item, depth + 1);
      }
    }
  }
  walk(raw, 0);
  const document = raw as EmbeddedModelDocument;
  if (document?.asset?.version !== "2.0" || !Array.isArray(document.buffers) || document.buffers.length !== 1 || !Number.isSafeInteger(document.buffers[0].byteLength) || document.buffers[0].byteLength < 1 || document.buffers[0].byteLength > binaryLength || binaryLength - document.buffers[0].byteLength > 3 || !Array.isArray(document.meshes) || !document.meshes.length) fail();
  if ((document.materials?.length ?? 0) > 128 || (document.textures?.length ?? 0) > 32 || (document.images?.length ?? 0) > 32) fail("This model has too many materials or textures for browser preview.");
  for (const item of document.bufferViews ?? []) if (item.buffer !== 0 || !Number.isSafeInteger(item.byteLength) || item.byteLength < 1 || !Number.isSafeInteger(item.byteOffset ?? 0) || (item.byteOffset ?? 0) < 0 || (item.byteOffset ?? 0) + item.byteLength > document.buffers[0].byteLength) fail();
  for (const image of document.images ?? []) if (!Number.isSafeInteger(image.bufferView) || !document.bufferViews?.[image.bufferView] || !["image/png", "image/jpeg"].includes(image.mimeType)) fail();
  let primitives = 0; let triangles = 0;
  for (const mesh of document.meshes) {
    if (!Array.isArray(mesh.primitives)) fail();
    for (const primitive of mesh.primitives) {
      primitives++;
      const count = document.accessors?.[primitive.indices ?? primitive.attributes?.POSITION]?.count;
      if (typeof count !== "number") return fail();
      if (!Number.isSafeInteger(count) || !count || count < 0 || (primitive.mode !== undefined && primitive.mode !== 4) || count % 3) fail();
      triangles += count / 3;
    }
  }
  if (primitives > 256 || triangles > 1_500_000) fail("This model is too complex for browser preview. The original remains in Assets.");
  return { document, binaryOffset: binaryHeader + 8, triangles };
}
